/**
 * Generador de Excel "contable" para el Resumen económico del wizard.
 *
 * Reemplaza al CSV legacy (eliminado 2026-05-09). Tres hojas:
 *   1. Resumen      — vista contable presentable (identificación + parámetros
 *                     + resumen por fase + total con monto en letras).
 *   2. Calendario   — opcional, solo si pagos_diferidos.meses > 0 Y subtotal
 *                     construccion > 0. Una fila por mensualidad con # /
 *                     Fecha / Monto / Acumulado. Freeze pane en row 1.
 *   3. Datos crudos — long format equivalente al CSV original
 *                     (Categoria/Concepto/Valor/Unidad/Formula/Notas) para
 *                     alimentar BI/scripts/pivot.
 *
 * Estilos contables alineados al branding Elawan: azul `#1F3540` y amarillo
 * `#F8CB38`. Headers de bloque en azul/amarillo, headers de tabla en
 * amarillo/azul, filas alternadas zebra-striping suave, total con caja
 * azul + monto amarillo.
 *
 * `exceljs` se carga via dynamic import en el helper `download…` para
 * mantener el bundle inicial liviano (la librería pesa ~700KB).
 */

import type ExcelJS from 'exceljs';
import { perfilMarca } from '@aosenuma/lib-types';
import type { ModeloNegocio, ResumenEconomicoContext } from './modeloNegocio';
import { numeroALetras, buildCalendarioPagos } from './resumenPdfHelpers';

/* ──────────────────────────────────────────────────────────────────────
 * Paleta + helpers de estilo
 * ──────────────────────────────────────────────────────────────────── */

// argb (alpha-rojo-verde-azul) — formato de exceljs.
const ARGB = {
  azulElawan: 'FF1F3540',
  amarilloElawan: 'FFF8CB38',
  amarilloDark: 'FFE0B420',
  blanco: 'FFFFFFFF',
  zebra: 'FFFAFBFB',
  borderSoft: 'FFE3E7E9',
  borderUltra: 'FFF0F2F3',
  textoFaint: 'FF7A8290',
} as const;

const NUMFMT = {
  mxn: '"$"#,##0.00;[Red]-"$"#,##0.00',
  mxnEntero: '"$"#,##0;[Red]-"$"#,##0',
  hectarea: '0.0000" ha"',
  fecha: 'dd-mmm-yyyy',
  numero2: '#,##0.00',
  entero: '#,##0',
  /** Teléfono MX 10 dígitos: "4421234567" → "442 123 4567". */
  telefonoMx: '### ### ####',
} as const;

/**
 * Sanitiza un teléfono mexicano a integer de 10 dígitos. Acepta los formatos
 * usuales: '4421234567', '442 123 4567', '442-123-4567', '+52 442 123 4567',
 * '521 442 123 4567' (LADA + 1 móvil). Devuelve null si la cantidad de
 * dígitos no calza con ningún patrón MX conocido — el caller cae a string.
 *
 * Por qué Number (no string): Excel marca strings con dígitos como
 * "número guardado como texto" (warning verde) y dificulta filtrar/ordenar.
 * El custom format `### ### ####` lo presenta visualmente como "442 123 4567"
 * pero la celda almacena 4421234567 y se comporta como número.
 */
function parseTelefonoMx(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const digits = String(raw).replace(/\D/g, '');
  let mx10: string;
  if (digits.length === 10) mx10 = digits;
  else if (digits.length === 11 && digits.startsWith('1')) mx10 = digits.slice(1);
  else if (digits.length === 12 && digits.startsWith('52')) mx10 = digits.slice(2);
  else if (digits.length === 13 && digits.startsWith('521')) mx10 = digits.slice(3);
  else return null;
  // Sanity: 10 dígitos sin ceros líderes problemáticos (los teléfonos MX no
  // empiezan en 0).
  if (mx10.startsWith('0')) return null;
  return Number(mx10);
}

function setBlockHeader(cell: ExcelJS.Cell, text: string): void {
  cell.value = text;
  cell.font = { name: 'Helvetica', size: 11, bold: true, color: { argb: ARGB.amarilloElawan } };
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.azulElawan } };
  cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
}

function setTableHeader(cell: ExcelJS.Cell, text: string): void {
  cell.value = text;
  cell.font = { name: 'Helvetica', size: 9, bold: true, color: { argb: ARGB.azulElawan } };
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.amarilloElawan } };
  cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  cell.border = {
    top: { style: 'thin', color: { argb: ARGB.amarilloDark } },
    bottom: { style: 'thin', color: { argb: ARGB.amarilloDark } },
  };
}

/** Aplica zebra striping + bordes finos a una row de datos. */
function styleDataRow(row: ExcelJS.Row, isAlt: boolean): void {
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.font = { name: 'Helvetica', size: 9 };
    if (isAlt) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.zebra } };
    }
    cell.border = {
      bottom: { style: 'hair', color: { argb: ARGB.borderUltra } },
    };
    cell.alignment = { vertical: 'middle' };
  });
}

/** Convierte ISO YYYY-MM-DD a Date anclada al mediodía UTC (evita drift de timezone). */
function parseIsoDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(`${iso}T12:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* ──────────────────────────────────────────────────────────────────────
 * Hoja 1: Resumen (vista contable principal)
 * ──────────────────────────────────────────────────────────────────── */

function buildHojaResumen(
  ws: ExcelJS.Worksheet,
  modelo: ModeloNegocio,
  ctx: ResumenEconomicoContext,
): void {
  // Anchos: 4 columnas (label + valor numérico + unidad + fórmula/notas).
  ws.columns = [
    { width: 28 }, // A — labels
    { width: 18 }, // B — valor
    { width: 10 }, // C — unidad
    { width: 36 }, // D — fórmula/notas
  ];

  let r = 1;

  /* ── Bloque 1: identificación ─────────────────────────────── */
  ws.mergeCells(r, 1, r, 4);
  setBlockHeader(
    ws.getCell(r, 1),
    `${perfilMarca().clienteNombre.toUpperCase()} · RESUMEN ECONÓMICO`,
  );
  ws.getRow(r).height = 26;
  r += 2;

  const identRows: Array<[string, string | number | Date | null | undefined, string?]> = [
    [
      'Parcela',
      ctx.num_parcela != null
        ? `Parcela ${ctx.num_parcela}${ctx.parcela_id ? ` · ${ctx.parcela_id}` : ''}`
        : ctx.parcela_id ?? '—',
    ],
    ['Ejido / Núcleo', ctx.nom_nucleo ?? '—'],
    ['Municipio', ctx.municipio ?? '—'],
    ['Área registral', ctx.area_ha ?? null, NUMFMT.hectarea],
    ['Titular', ctx.titular_completo ?? ctx.titular_masked ?? '—'],
    // Teléfono: si parsea a 10 dígitos MX, va como Number con formato visual
    // "442 123 4567"; si no, fallback al string original.
    (() => {
      const tel = parseTelefonoMx(ctx.telefono);
      return tel != null
        ? (['Teléfono', tel, NUMFMT.telefonoMx] as [string, number, string])
        : (['Teléfono', ctx.telefono ?? '—'] as [string, string]);
    })(),
    ['Folio de tierras', ctx.folio_tierras ?? '—'],
    ['Folio de derechos', ctx.folio_derechos ?? '—'],
    ['Clave catastral', ctx.clave_catastral ?? '—'],
    [
      'Fecha estimada de firma',
      parseIsoDate(ctx.fecha_firma_est) ?? (ctx.fecha_firma_est ? '—' : 'Por definir'),
      NUMFMT.fecha,
    ],
  ];
  for (const [label, value, fmt] of identRows) {
    const labelCell = ws.getCell(r, 1);
    labelCell.value = label;
    labelCell.font = { name: 'Helvetica', size: 9, bold: true, color: { argb: ARGB.azulElawan } };
    labelCell.alignment = { horizontal: 'left', vertical: 'middle' };

    const valueCell = ws.getCell(r, 2);
    valueCell.value = value as ExcelJS.CellValue;
    valueCell.font = { name: 'Helvetica', size: 9 };
    if (fmt) valueCell.numFmt = fmt;
    valueCell.alignment = { horizontal: 'left', vertical: 'middle' };

    r++;
  }
  r += 1; // espaciador

  /* ── Bloque 2: parámetros capturados ──────────────────────── */
  ws.mergeCells(r, 1, r, 4);
  setBlockHeader(ws.getCell(r, 1), 'PARÁMETROS CAPTURADOS');
  ws.getRow(r).height = 22;
  r++;

  setTableHeader(ws.getCell(r, 1), 'Concepto');
  setTableHeader(ws.getCell(r, 2), 'Valor');
  setTableHeader(ws.getCell(r, 3), 'Unidad');
  setTableHeader(ws.getCell(r, 4), 'Notas');
  ws.getRow(r).height = 18;
  r++;

  // ParamKey indexa el address (B<row>) de cada input dentro del bloque, para
  // que las fases (bloque 3) y el total (bloque 4) puedan armar fórmulas
  // tipo `=B6*B7` referenciando las celdas reales y no valores hardcoded.
  type ParamKey =
    | 'superficiePreliminar'
    | 'tarifaPreliminar'
    | 'bonoFirma'
    | 'tarifaConstruccion'
    | 'cantidadUnidades'
    | 'plazoConstruccion'
    | 'meses';
  type ParamRow = {
    key: ParamKey;
    label: string;
    value: number | string | null;
    unit?: string;
    notes?: string;
    fmt?: string;
  };
  const paramRows: ParamRow[] = [
    {
      key: 'superficiePreliminar',
      label: 'Superficie preliminar',
      value: modelo.preliminar.superficie_ha,
      unit: 'ha',
      fmt: NUMFMT.numero2,
      notes: 'Hectáreas objeto del pago preliminar',
    },
    {
      key: 'tarifaPreliminar',
      label: 'Tarifa preliminar',
      value: modelo.preliminar.tarifa_mxn_por_ha,
      unit: 'MXN/ha',
      fmt: NUMFMT.mxn,
      notes: 'Constante del modelo',
    },
    {
      key: 'bonoFirma',
      label: 'Bono de firma',
      value: modelo.bono_firma.monto_mxn,
      unit: 'MXN',
      fmt: NUMFMT.mxn,
      notes: 'Pago único opcional al firmar',
    },
    {
      key: 'tarifaConstruccion',
      label: 'Tarifa construcción',
      value: modelo.construccion_operacion.tarifa_mxn_por_unidad,
      unit: 'MXN/u',
      fmt: NUMFMT.mxn,
      notes: 'Tarifa por MW o instalación',
    },
    {
      key: 'cantidadUnidades',
      label: 'Cantidad de unidades',
      value: modelo.construccion_operacion.cantidad_unidades,
      unit: 'unid.',
      fmt: NUMFMT.entero,
      notes: 'MW / instalaciones planeadas',
    },
    {
      key: 'plazoConstruccion',
      label: 'Plazo construcción y operación',
      value: modelo.construccion_operacion.plazo_anios,
      unit: 'años',
      fmt: NUMFMT.entero,
      notes: 'Vigencia del contrato',
    },
  ];
  if (modelo.pagos_diferidos.meses != null && modelo.pagos_diferidos.meses > 0) {
    paramRows.push({
      key: 'meses',
      label: 'Diferimiento',
      value: modelo.pagos_diferidos.meses,
      unit: 'meses',
      fmt: NUMFMT.entero,
      notes: modelo.pagos_diferidos.seguimiento_mensual ? 'con seguimiento mensual' : '0 = al contado',
    });
  }
  const paramAddr: Partial<Record<ParamKey, string>> = {};
  paramRows.forEach((p, idx) => {
    ws.getCell(r, 1).value = p.label;
    ws.getCell(r, 2).value = p.value as ExcelJS.CellValue;
    if (p.fmt) ws.getCell(r, 2).numFmt = p.fmt;
    ws.getCell(r, 2).alignment = { horizontal: 'right', vertical: 'middle' };
    paramAddr[p.key] = ws.getCell(r, 2).address;
    ws.getCell(r, 3).value = p.unit ?? '';
    ws.getCell(r, 3).font = { name: 'Helvetica', size: 8.5, color: { argb: ARGB.textoFaint } };
    ws.getCell(r, 4).value = p.notes ?? '';
    ws.getCell(r, 4).font = { name: 'Helvetica', size: 8.5, color: { argb: ARGB.textoFaint } };
    styleDataRow(ws.getRow(r), idx % 2 === 1);
    r++;
  });
  r += 1;

  /* ── Bloque 3: resumen por fase ───────────────────────────── */
  ws.mergeCells(r, 1, r, 4);
  setBlockHeader(ws.getCell(r, 1), 'RESUMEN POR FASE');
  ws.getRow(r).height = 22;
  r++;

  setTableHeader(ws.getCell(r, 1), 'Fase');
  setTableHeader(ws.getCell(r, 2), 'Monto');
  setTableHeader(ws.getCell(r, 3), '');
  setTableHeader(ws.getCell(r, 4), 'Fórmula');
  ws.getRow(r).height = 18;
  r++;

  // Cada fase guarda su address (B<row>) en faseAddr para que el bloque 4
  // pueda armar `=B<preliminar>+B<bono>+B<construccion>` y el contador
  // pueda hacer click en el total → barra de fórmulas muestra de dónde sale.
  // El campo `formula` es ExcelJS.CellValue: si tenemos addresses de los
  // factores, vamos por `{ formula, result }`; si no (parcela sin captura),
  // caemos al número crudo para no producir #REF! en Excel.
  type FaseKey = 'preliminar' | 'bonoFirma' | 'construccion' | 'mensualidad';
  type FaseRow = {
    key: FaseKey;
    name: string;
    cellValue: ExcelJS.CellValue;
    formulaText: string;
  };
  const preliminarFormula: ExcelJS.CellValue =
    paramAddr.superficiePreliminar && paramAddr.tarifaPreliminar
      ? {
          formula: `${paramAddr.superficiePreliminar}*${paramAddr.tarifaPreliminar}`,
          result: modelo.preliminar.monto_mxn ?? 0,
        }
      : (modelo.preliminar.monto_mxn as ExcelJS.CellValue);
  const bonoFormula: ExcelJS.CellValue = paramAddr.bonoFirma
    ? { formula: paramAddr.bonoFirma, result: modelo.bono_firma.monto_mxn ?? 0 }
    : (modelo.bono_firma.monto_mxn as ExcelJS.CellValue);
  const construccionFormula: ExcelJS.CellValue =
    paramAddr.tarifaConstruccion && paramAddr.cantidadUnidades
      ? {
          formula: `${paramAddr.tarifaConstruccion}*${paramAddr.cantidadUnidades}`,
          result: modelo.construccion_operacion.subtotal_mxn ?? 0,
        }
      : (modelo.construccion_operacion.subtotal_mxn as ExcelJS.CellValue);

  const fases: FaseRow[] = [
    {
      key: 'preliminar',
      name: 'Preliminar',
      cellValue: preliminarFormula,
      formulaText:
        modelo.preliminar.superficie_ha != null
          ? `${modelo.preliminar.superficie_ha} ha × ${modelo.preliminar.tarifa_mxn_por_ha} MXN/ha`
          : 'sin captura',
    },
    {
      key: 'bonoFirma',
      name: 'Bono de firma',
      cellValue: bonoFormula,
      formulaText: 'captura directa',
    },
    {
      key: 'construccion',
      name: 'Construcción y operación',
      cellValue: construccionFormula,
      formulaText:
        modelo.construccion_operacion.cantidad_unidades != null
          ? `${modelo.construccion_operacion.tarifa_mxn_por_unidad} MXN × ${modelo.construccion_operacion.cantidad_unidades} unid.`
          : 'sin captura',
    },
  ];
  // Mensualidad NO entra al total (es derivada del subtotal construcción) —
  // solo se muestra como referencia de cuánto se pagaría por mes. Su fórmula
  // referencia el subtotal construcción una vez que sepamos su address; lo
  // injectamos abajo dentro del forEach.
  let pendingMensualidad: { meses: number; monto: number } | null = null;
  if (
    modelo.pagos_diferidos.monto_mensual_mxn != null &&
    modelo.pagos_diferidos.meses != null &&
    modelo.pagos_diferidos.meses > 0
  ) {
    pendingMensualidad = {
      meses: modelo.pagos_diferidos.meses,
      monto: modelo.pagos_diferidos.monto_mensual_mxn,
    };
  }

  const faseAddr: Partial<Record<FaseKey, string>> = {};
  fases.forEach((f, idx) => {
    ws.getCell(r, 1).value = f.name;
    ws.getCell(r, 2).value = f.cellValue;
    ws.getCell(r, 2).numFmt = NUMFMT.mxn;
    ws.getCell(r, 2).alignment = { horizontal: 'right', vertical: 'middle' };
    faseAddr[f.key] = ws.getCell(r, 2).address;
    ws.getCell(r, 4).value = f.formulaText;
    ws.getCell(r, 4).font = { name: 'Helvetica', size: 8.5, color: { argb: ARGB.textoFaint } };
    styleDataRow(ws.getRow(r), idx % 2 === 1);
    r++;
  });
  // Mensualidad como fila adicional (sin sumar al total). Fórmula referencia
  // el subtotal construcción capturado arriba + la celda de meses si existe.
  if (pendingMensualidad) {
    ws.getCell(r, 1).value = `Mensualidad (${pendingMensualidad.meses} meses)`;
    ws.getCell(r, 2).value =
      faseAddr.construccion && paramAddr.meses
        ? { formula: `${faseAddr.construccion}/${paramAddr.meses}`, result: pendingMensualidad.monto }
        : (pendingMensualidad.monto as ExcelJS.CellValue);
    ws.getCell(r, 2).numFmt = NUMFMT.mxn;
    ws.getCell(r, 2).alignment = { horizontal: 'right', vertical: 'middle' };
    faseAddr.mensualidad = ws.getCell(r, 2).address;
    ws.getCell(r, 4).value = `subtotal construcción ÷ ${pendingMensualidad.meses}`;
    ws.getCell(r, 4).font = { name: 'Helvetica', size: 8.5, color: { argb: ARGB.textoFaint } };
    styleDataRow(ws.getRow(r), fases.length % 2 === 1);
    r++;
  }
  r += 1;

  /* ── Bloque 4: total a pagar (caja azul + monto amarillo + en letras) ── */
  ws.mergeCells(r, 1, r, 4);
  const totalLabelCell = ws.getCell(r, 1);
  totalLabelCell.value = 'TOTAL A PAGAR';
  totalLabelCell.font = { name: 'Helvetica', size: 11, bold: true, color: { argb: ARGB.amarilloElawan } };
  totalLabelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.azulElawan } };
  totalLabelCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  ws.getRow(r).height = 24;
  r++;

  const totalRow = ws.getRow(r);
  ws.getCell(r, 1).value = 'Suma de fases comprometidas';
  ws.getCell(r, 1).font = { name: 'Helvetica', size: 10, color: { argb: ARGB.blanco } };
  ws.getCell(r, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.azulElawan } };
  ws.getCell(r, 1).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  ws.mergeCells(r, 2, r, 4);
  // Fórmula del total: suma de las 3 fases comprometidas (preliminar + bono +
  // construcción). Click en la celda → barra de fórmulas muestra
  // `=B<preliminar>+B<bono>+B<construccion>`. Mensualidad NO se suma (es
  // derivada). Fallback: si por alguna razón no capturamos addresses,
  // ponemos el número crudo para no romper el render.
  const totalAddrs = [faseAddr.preliminar, faseAddr.bonoFirma, faseAddr.construccion].filter(
    (a): a is string => !!a,
  );
  ws.getCell(r, 2).value =
    totalAddrs.length >= 2
      ? {
          formula: totalAddrs.join('+'),
          result: modelo.totales.suma_componentes_mxn ?? 0,
        }
      : (modelo.totales.suma_componentes_mxn as ExcelJS.CellValue);
  ws.getCell(r, 2).numFmt = NUMFMT.mxn;
  ws.getCell(r, 2).font = { name: 'Helvetica', size: 18, bold: true, color: { argb: ARGB.amarilloElawan } };
  ws.getCell(r, 2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.azulElawan } };
  ws.getCell(r, 2).alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
  totalRow.height = 30;
  r++;

  ws.mergeCells(r, 1, r, 4);
  const letrasCell = ws.getCell(r, 1);
  letrasCell.value = numeroALetras(modelo.totales.suma_componentes_mxn);
  letrasCell.font = { name: 'Helvetica', size: 9, italic: true, color: { argb: 'CCFFFFFF' } };
  letrasCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ARGB.azulElawan } };
  letrasCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
  ws.getRow(r).height = 16;
  r++;

  // Print setup: A4 portrait, fit to 1 page wide
  ws.pageSetup = {
    paperSize: 9, // A4
    orientation: 'portrait',
    fitToPage: true,
    fitToWidth: 1,
    margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
  };
  ws.headerFooter = {
    oddFooter: `&L&8${perfilMarca().pieDocumento}&R&8Página &P de &N`,
  };
}

/* ──────────────────────────────────────────────────────────────────────
 * Hoja 2: Calendario de pagos
 * ──────────────────────────────────────────────────────────────────── */

function buildHojaCalendario(
  ws: ExcelJS.Worksheet,
  modelo: ModeloNegocio,
  ctx: ResumenEconomicoContext,
): void {
  ws.columns = [
    { width: 6 },   // A — #
    { width: 16 },  // B — fecha
    { width: 16 },  // C — monto
    { width: 16 },  // D — acumulado
  ];

  setTableHeader(ws.getCell(1, 1), '#');
  setTableHeader(ws.getCell(1, 2), 'Fecha');
  setTableHeader(ws.getCell(1, 3), 'Monto');
  setTableHeader(ws.getCell(1, 4), 'Acumulado');
  ws.getRow(1).height = 18;

  const pagos = buildCalendarioPagos({
    subtotalConstruccion: modelo.construccion_operacion.subtotal_mxn,
    meses: modelo.pagos_diferidos.meses,
    fechaFirmaIso: ctx.fecha_firma_est ?? null,
  });

  pagos.forEach((p, idx) => {
    const r = idx + 2;
    ws.getCell(r, 1).value = p.num;
    ws.getCell(r, 1).numFmt = NUMFMT.entero;
    ws.getCell(r, 1).alignment = { horizontal: 'right', vertical: 'middle' };

    const fechaParsed = parseIsoDate(p.fechaIso);
    if (fechaParsed) {
      ws.getCell(r, 2).value = fechaParsed;
      ws.getCell(r, 2).numFmt = NUMFMT.fecha;
    } else {
      ws.getCell(r, 2).value = p.fechaLabel;
    }
    ws.getCell(r, 2).alignment = { horizontal: 'left', vertical: 'middle' };

    ws.getCell(r, 3).value = p.monto;
    ws.getCell(r, 3).numFmt = NUMFMT.mxn;
    ws.getCell(r, 3).alignment = { horizontal: 'right', vertical: 'middle' };

    // Acumulado como fórmula running: D2 = C2; D<r> = D<r-1> + C<r>.
    // Click en cualquier acumulado → barra de fórmulas explica de dónde
    // sale (suma del acumulado anterior + el monto del mes).
    ws.getCell(r, 4).value =
      idx === 0
        ? { formula: `C${r}`, result: p.acumulado }
        : { formula: `D${r - 1}+C${r}`, result: p.acumulado };
    ws.getCell(r, 4).numFmt = NUMFMT.mxn;
    ws.getCell(r, 4).alignment = { horizontal: 'right', vertical: 'middle' };

    styleDataRow(ws.getRow(r), idx % 2 === 1);
  });

  // Freeze pane: header siempre visible
  ws.views = [{ state: 'frozen', ySplit: 1, xSplit: 0 }];

  // AutoFilter sobre el rango de datos (incluye header)
  if (pagos.length > 0) {
    ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: pagos.length + 1, column: 4 } };
  }

  ws.pageSetup = {
    paperSize: 9,
    orientation: 'portrait',
    fitToPage: true,
    fitToWidth: 1,
    margins: { left: 0.4, right: 0.4, top: 0.5, bottom: 0.5, header: 0.2, footer: 0.2 },
  };
  ws.headerFooter = {
    oddFooter: `&L&8${perfilMarca().pieDocumento}&R&8Página &P de &N`,
  };
}

/* ──────────────────────────────────────────────────────────────────────
 * Hoja 3: Datos crudos (long format, pivot-ready)
 * ──────────────────────────────────────────────────────────────────── */

function buildHojaDatosCrudos(
  ws: ExcelJS.Worksheet,
  modelo: ModeloNegocio,
  ctx: ResumenEconomicoContext,
): void {
  ws.columns = [
    { width: 14 }, // Categoria
    { width: 28 }, // Concepto
    { width: 14 }, // Valor
    { width: 10 }, // Unidad
    { width: 26 }, // Formula
    { width: 36 }, // Notas
  ];

  setTableHeader(ws.getCell(1, 1), 'Categoría');
  setTableHeader(ws.getCell(1, 2), 'Concepto');
  setTableHeader(ws.getCell(1, 3), 'Valor');
  setTableHeader(ws.getCell(1, 4), 'Unidad');
  setTableHeader(ws.getCell(1, 5), 'Fórmula');
  setTableHeader(ws.getCell(1, 6), 'Notas');
  ws.getRow(1).height = 18;

  type CrudaRow = {
    cat: string;
    concepto: string;
    valor: number | string | Date | null;
    unidad: string;
    formula: string;
    notas: string;
    /** Si el valor es número/fecha, qué numFmt aplicar. */
    fmt?: string;
  };
  const rows: CrudaRow[] = [
    // Identificación
    {
      cat: 'identificacion',
      concepto: 'parcela_id',
      valor: ctx.parcela_id ?? '',
      unidad: '',
      formula: '',
      notas: 'ID público inmutable',
    },
    {
      cat: 'identificacion',
      concepto: 'num_parcela',
      valor: ctx.num_parcela != null ? String(ctx.num_parcela) : '',
      unidad: '',
      formula: '',
      notas: 'Número RAN (puede tener sufijo letra)',
    },
    {
      cat: 'identificacion',
      concepto: 'nom_nucleo',
      valor: ctx.nom_nucleo ?? '',
      unidad: '',
      formula: '',
      notas: '',
    },
    {
      cat: 'identificacion',
      concepto: 'municipio',
      valor: ctx.municipio ?? '',
      unidad: '',
      formula: '',
      notas: '',
    },
    {
      cat: 'identificacion',
      concepto: 'area_ha_registral',
      valor: ctx.area_ha ?? null,
      unidad: 'ha',
      formula: '',
      notas: 'Área del polígono RAN, no necesariamente la negociada',
      fmt: NUMFMT.numero2,
    },
    {
      cat: 'identificacion',
      concepto: 'titular_masked',
      valor: ctx.titular_masked ?? '',
      unidad: '',
      formula: '',
      notas: 'Plaintext seguro derivado del nombre completo',
    },
    {
      cat: 'identificacion',
      concepto: 'titular_completo',
      valor: ctx.titular_completo ?? '',
      unidad: '',
      formula: '',
      notas: 'PII descifrada — solo presente si MFA del técnico vigente',
    },
    (() => {
      const tel = parseTelefonoMx(ctx.telefono);
      return {
        cat: 'identificacion',
        concepto: 'telefono',
        valor: tel != null ? tel : (ctx.telefono ?? ''),
        unidad: '',
        formula: '',
        notas: 'PII descifrada — contacto del titular',
        fmt: tel != null ? NUMFMT.telefonoMx : undefined,
      };
    })(),
    {
      cat: 'identificacion',
      concepto: 'folio_tierras',
      valor: ctx.folio_tierras ?? '',
      unidad: '',
      formula: '',
      notas: 'RAN — folio agrario de tierras',
    },
    {
      cat: 'identificacion',
      concepto: 'folio_derechos',
      valor: ctx.folio_derechos ?? '',
      unidad: '',
      formula: '',
      notas: 'RAN — folio de certificado de derechos',
    },
    {
      cat: 'identificacion',
      concepto: 'clave_catastral',
      valor: ctx.clave_catastral ?? '',
      unidad: '',
      formula: '',
      notas: 'Cadastro municipal',
    },
    // Compromiso
    {
      cat: 'compromiso',
      concepto: 'fecha_firma_estimada',
      valor: parseIsoDate(ctx.fecha_firma_est) ?? (ctx.fecha_firma_est ? '' : ''),
      unidad: '',
      formula: '',
      notas: 'Hito comercial clave (capturado en wizard paso 3)',
      fmt: NUMFMT.fecha,
    },
    // Inputs del cálculo
    {
      cat: 'input',
      concepto: 'superficie_preliminar',
      valor: modelo.preliminar.superficie_ha,
      unidad: 'ha',
      formula: '',
      notas: 'Hectáreas objeto del pago preliminar',
      fmt: NUMFMT.numero2,
    },
    {
      cat: 'input',
      concepto: 'tarifa_preliminar_por_ha',
      valor: modelo.preliminar.tarifa_mxn_por_ha,
      unidad: 'MXN/ha',
      formula: '',
      notas: 'Constante PRELIMINAR_MXN_POR_HA',
      fmt: NUMFMT.mxn,
    },
    {
      cat: 'input',
      concepto: 'bono_firma',
      valor: modelo.bono_firma.monto_mxn,
      unidad: 'MXN',
      formula: '',
      notas: 'Pago único opcional al firmar',
      fmt: NUMFMT.mxn,
    },
    {
      cat: 'input',
      concepto: 'tarifa_construccion_unidad',
      valor: modelo.construccion_operacion.tarifa_mxn_por_unidad,
      unidad: 'MXN/u',
      formula: '',
      notas: 'Tarifa por MW o instalación',
      fmt: NUMFMT.mxn,
    },
    {
      cat: 'input',
      concepto: 'cantidad_unidades_construccion',
      valor: modelo.construccion_operacion.cantidad_unidades,
      unidad: 'unid.',
      formula: '',
      notas: 'MW / instalaciones planeadas',
      fmt: NUMFMT.entero,
    },
    {
      cat: 'input',
      concepto: 'plazo_construccion_operacion',
      valor: modelo.construccion_operacion.plazo_anios,
      unidad: 'años',
      formula: '',
      notas: 'Vigencia del contrato',
      fmt: NUMFMT.entero,
    },
    {
      cat: 'input',
      concepto: 'diferir_meses',
      valor: modelo.pagos_diferidos.meses,
      unidad: 'meses',
      formula: '',
      notas: '0 o vacío = al contado',
      fmt: NUMFMT.entero,
    },
    {
      cat: 'input',
      concepto: 'seguimiento_mensual',
      valor: modelo.pagos_diferidos.seguimiento_mensual ? 1 : 0,
      unidad: 'bool',
      formula: '',
      notas: 'Toggle metadata, no afecta cálculos',
      fmt: NUMFMT.entero,
    },
    // Outputs
    {
      cat: 'output',
      concepto: 'preliminar_subtotal',
      valor: modelo.preliminar.monto_mxn,
      unidad: 'MXN',
      formula:
        modelo.preliminar.superficie_ha != null
          ? `${modelo.preliminar.superficie_ha} * ${modelo.preliminar.tarifa_mxn_por_ha}`
          : '',
      notas: '',
      fmt: NUMFMT.mxn,
    },
    {
      cat: 'output',
      concepto: 'bono_firma_subtotal',
      valor: modelo.bono_firma.monto_mxn,
      unidad: 'MXN',
      formula: '',
      notas: 'Captura directa, sin fórmula',
      fmt: NUMFMT.mxn,
    },
    {
      cat: 'output',
      concepto: 'construccion_subtotal',
      valor: modelo.construccion_operacion.subtotal_mxn,
      unidad: 'MXN',
      formula:
        modelo.construccion_operacion.cantidad_unidades != null
          ? `${modelo.construccion_operacion.tarifa_mxn_por_unidad} * ${modelo.construccion_operacion.cantidad_unidades}`
          : '',
      notas: '',
      fmt: NUMFMT.mxn,
    },
    {
      cat: 'output',
      concepto: 'mensualidad',
      valor: modelo.pagos_diferidos.monto_mensual_mxn,
      unidad: 'MXN',
      formula:
        modelo.pagos_diferidos.meses != null
          ? `subtotal_construccion / ${modelo.pagos_diferidos.meses}`
          : '',
      notas: 'Cero/vacío si no hay diferimiento',
      fmt: NUMFMT.mxn,
    },
    // Total
    {
      cat: 'output',
      concepto: 'total_componentes',
      valor: modelo.totales.suma_componentes_mxn,
      unidad: 'MXN',
      formula: 'preliminar + bono_firma + construccion',
      notas: 'Total a pagar',
      fmt: NUMFMT.mxn,
    },
    // Metadata
    {
      cat: 'meta',
      concepto: 'moneda',
      valor: modelo.moneda,
      unidad: '',
      formula: '',
      notas: '',
    },
    {
      cat: 'meta',
      concepto: 'modelo_version',
      valor: modelo.version,
      unidad: '',
      formula: '',
      notas: '',
      fmt: NUMFMT.entero,
    },
    {
      cat: 'meta',
      concepto: 'export_iso',
      valor: new Date().toISOString(),
      unidad: '',
      formula: '',
      notas: 'Timestamp UTC de generación del Excel',
    },
  ];

  rows.forEach((d, idx) => {
    const r = idx + 2;
    ws.getCell(r, 1).value = d.cat;
    ws.getCell(r, 2).value = d.concepto;
    ws.getCell(r, 3).value = d.valor as ExcelJS.CellValue;
    if (d.fmt && d.valor != null) ws.getCell(r, 3).numFmt = d.fmt;
    ws.getCell(r, 3).alignment = { horizontal: 'right', vertical: 'middle' };
    ws.getCell(r, 4).value = d.unidad;
    ws.getCell(r, 5).value = d.formula;
    ws.getCell(r, 5).font = { name: 'Helvetica', size: 8.5, color: { argb: ARGB.textoFaint } };
    ws.getCell(r, 6).value = d.notas;
    ws.getCell(r, 6).font = { name: 'Helvetica', size: 8.5, color: { argb: ARGB.textoFaint } };
    styleDataRow(ws.getRow(r), idx % 2 === 1);
  });

  ws.views = [{ state: 'frozen', ySplit: 1, xSplit: 0 }];
  if (rows.length > 0) {
    ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: rows.length + 1, column: 6 } };
  }
}

/* ──────────────────────────────────────────────────────────────────────
 * Builder principal + helper de descarga
 * ──────────────────────────────────────────────────────────────────── */

/**
 * Genera el Excel del Resumen económico como Blob listo para descargar.
 *
 * Carga `exceljs` con dynamic import — la lib pesa ~700KB y no debería
 * impactar el bundle inicial del panel técnico.
 */
export async function buildResumenEconomicoXlsxBlob(
  modelo: ModeloNegocio,
  ctx: ResumenEconomicoContext = {},
): Promise<Blob> {
  // dynamic import para que exceljs no entre al bundle hasta que se exporte.
  const ExcelJSModule = await import('exceljs');
  const ExcelJSDefault = (ExcelJSModule as unknown as { default?: typeof ExcelJS }).default;
  const ExcelJSLib: typeof ExcelJS =
    ExcelJSDefault ?? (ExcelJSModule as unknown as typeof ExcelJS);

  const workbook = new ExcelJSLib.Workbook();
  workbook.creator = 'aosenuma';
  workbook.lastModifiedBy = 'aosenuma';
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.properties = { date1904: false };

  // Hoja 1: Resumen
  const wsResumen = workbook.addWorksheet('Resumen', {
    properties: { tabColor: { argb: 'FFF8CB38' } },
    views: [{ showGridLines: false }],
  });
  buildHojaResumen(wsResumen, modelo, ctx);

  // Hoja 2: Calendario (solo si aplica)
  const tieneCalendario =
    modelo.pagos_diferidos.meses != null &&
    modelo.pagos_diferidos.meses > 0 &&
    modelo.construccion_operacion.subtotal_mxn != null &&
    modelo.construccion_operacion.subtotal_mxn > 0;
  if (tieneCalendario) {
    const wsCal = workbook.addWorksheet('Calendario de pagos', {
      properties: { tabColor: { argb: 'FF1F3540' } },
    });
    buildHojaCalendario(wsCal, modelo, ctx);
  }

  // Hoja 3: Datos crudos
  const wsCrudos = workbook.addWorksheet('Datos crudos', {
    properties: { tabColor: { argb: 'FF7A8290' } },
  });
  buildHojaDatosCrudos(wsCrudos, modelo, ctx);

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/**
 * Dispara descarga del Excel del Resumen económico desde el browser.
 * No persiste server-side — el caller puede emitir audit log aparte.
 */
export async function downloadResumenEconomicoXlsx(
  modelo: ModeloNegocio,
  ctx: ResumenEconomicoContext = {},
): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const blob = await buildResumenEconomicoXlsxBlob(modelo, ctx);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  const tag = ctx.parcela_id ? `-${ctx.parcela_id}` : '';
  a.download = `resumen-economico${tag}-${stamp}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
